import React, { useMemo } from "react";
import useSWR from "swr";
import { KeyRound, CheckCircle2, Clock, Lock } from "lucide-react";
import { toast } from "sonner";
import { api, fmtDate } from "@/lib/api";
import { Skeleton } from "@/components/Atoms";

const fetcher = (u) => api.get(u).then((r) => r.data);

export default function BuyerHandover() {
  const { data } = useSWR("/me/handover", fetcher);

  const grouped = useMemo(() => {
    const g = {};
    (data?.items || []).forEach((it) => {
      g[it.category] = g[it.category] || [];
      g[it.category].push(it);
    });
    return g;
  }, [data]);

  const pct = data?.pct || 0;
  const allDone = pct === 100;

  return (
    <div className="space-y-5">
      <div>
        <div className="rc-title-1">Handover Status</div>
        <div className="rc-callout">Your possession checklist — civil, electrical, plumbing &amp; documentation.</div>
      </div>

      {!data && <Skeleton h={120}/>}
      {data && (
        <>
          <div className="rc-card rc-card-padded">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div>
                <div className="rc-title-3">{data.completed} of {data.total} items complete</div>
                <div className="rc-footnote mt-1">{pct}% ready for handover</div>
              </div>
              <div className="rc-countdown rc-tabular" style={{ color: allDone ? "var(--rc-success)" : "var(--rc-gold)" }}>{pct}%</div>
            </div>
            <div className="mt-3 h-2 bg-[var(--rc-near-white)] rounded-full overflow-hidden">
              <div className="h-full" style={{ width: `${pct}%`, background: "linear-gradient(90deg, var(--rc-orange), var(--rc-gold))", transition: "width 800ms ease" }}/>
            </div>
          </div>

          {Object.entries(grouped).map(([cat, items]) => {
            const done = items.filter(i => i.status === "completed").length;
            return (
              <div key={cat} className="rc-card rc-card-padded">
                <div className="flex items-center justify-between mb-3">
                  <div className="rc-title-3">{cat}</div>
                  <div className="rc-footnote">{done}/{items.length}</div>
                </div>
                <div className="divide-y divide-[var(--rc-line)]">
                  {items.map((it) => (
                    <div key={it.item_id} className="py-3 flex items-center gap-3">
                      {it.status === "completed"
                        ? <CheckCircle2 className="text-[var(--rc-success)]" size={20}/>
                        : <Clock className="text-[var(--rc-warning)]" size={20}/>}
                      <div className="flex-1">
                        <div className="rc-body">{it.item_name}</div>
                        {it.completed_at && <div className="rc-caption">Done {fmtDate(it.completed_at)}</div>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}

          <div className="rc-card rc-card-padded text-center">
            {allDone ? (
              <>
                <KeyRound size={36} className="mx-auto text-[var(--rc-blue)] mb-2"/>
                <div className="rc-title-3">Your home is ready for handover</div>
                <div className="rc-callout mt-1">Your RM will schedule the keys ceremony shortly.</div>
                <button onClick={() => toast.success("Sign-off OTP sent")} className="rc-btn rc-btn-primary mt-4">OTP-based Acceptance</button>
              </>
            ) : (
              <>
                <Lock size={36} className="mx-auto text-[var(--rc-grey-icon)] mb-2"/>
                <div className="rc-title-3">Digital sign-off locked</div>
                <div className="rc-callout mt-1">Complete all checklist items to unlock handover.</div>
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
}
